/**
 * iter233 · One production slot row of the planning sheet.
 *
 * Cells are edited in place (click → type → Enter). Field rights follow the
 * user-approved matrix: Factory Head everything, Production Manager
 * operational fields, Supervisor actual output + remarks only.
 */
import React, { useState } from 'react';
import {
  ChevronDown, ChevronRight, MessageSquare, Pencil, Copy, CalendarDays,
  Ban, FlaskConical, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { fmtQty } from '../../utils/format';
import { canEditField, isFactoryHead, SHIFTS, PRIORITIES } from './planningColumns';

const RM_TONE = {
  ready: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  partial: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  short: 'bg-red-500/15 text-red-300 border-red-500/30',
  no_recipe: 'bg-zinc-800 text-zinc-500 border-zinc-700',
  unreserved: 'bg-zinc-800 text-zinc-400 border-zinc-700',
};

const STATUS_TONE = {
  closed: 'text-zinc-500',
  completed: 'text-emerald-400',
  partially_completed: 'text-amber-400',
  in_progress: 'text-blue-400',
  delayed: 'text-red-400',
  cancelled: 'text-zinc-500',
};

export default function PlanningSheetRow({ row, cols, role, expanded, onToggle, onPatch, onAction, isNew }) {
  const overdue = row.lifecycle === 'overdue';
  return (
    <>
      <tr className={`border-b border-zinc-800/60 hover:bg-zinc-800/30 transition-colors ${
        isNew ? 'bg-amber-500/15 ring-1 ring-amber-500/50' : overdue ? 'bg-red-500/[0.04]' : ''}`}
        data-testid={`fp-pw-row-${row.id}`}>
        <td className="px-2 py-1 sticky left-0 bg-zinc-900 z-[1] border-r border-zinc-800/60">
          <button onClick={onToggle} data-testid={`fp-pw-row-toggle-${row.id}`}
            className="text-zinc-500 hover:text-zinc-100">
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </td>
        <td className="px-2 py-1 sticky bg-zinc-900 z-[1]" style={{ left: 38 }}>
          <div className="flex items-center gap-1.5">
            <span className="text-zinc-100 whitespace-nowrap">{row.product_name}</span>
            {isNew && <span className="text-[9px] bg-amber-500 text-black px-1 rounded font-bold">NEW</span>}
            {row.at_risk && <AlertTriangle size={11} className="text-red-400 shrink-0" title="RM short" />}
            {(row.rule_flags || []).length > 0 && (
              <button onClick={onToggle} data-testid={`fp-pw-rulechip-${row.id}`}
                title={(row.rule_flags || []).map(f => `${f.title} — ${f.text}`).join('\n')}
                className={`text-[9px] px-1.5 rounded border shrink-0 ${row.rule_warnings
                  ? 'bg-red-500/15 text-red-300 border-red-500/40'
                  : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                ⚠ {row.rule_warnings || row.rule_flags.length} rule
              </button>
            )}
          </div>
          <span className="text-[10px] text-zinc-500">{row.id}</span>
        </td>
        {cols.map(c => (
          <SheetCell key={c.k} col={c} row={row} role={role} onPatch={onPatch} onAction={onAction} />
        ))}
      </tr>
      {expanded && <ExpandedDetail row={row} colspan={cols.length + 2} />}
    </>
  );
}

// ─── One cell — read view + inline editor ─────────────────────────────
function SheetCell({ col, row, role, onPatch, onAction }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const editable = !!col.edit && canEditField(role, col.k) && row.lifecycle !== 'cancelled';
  const align = col.align === 'right' ? 'text-right' : 'text-left';

  const commit = (value) => {
    setEditing(false);
    const before = raw(col.k, row);
    if (String(value ?? '') === String(before ?? '')) return;
    onPatch(col.k, value);
  };

  if (col.k === 'actions') {
    const fh = isFactoryHead(role);
    return (
      <td className="px-2 py-1 whitespace-nowrap" data-testid={`fp-pw-actions-${row.id}`}>
        <div className="flex items-center gap-1">
          <IconBtn t="Team notes" onClick={() => onAction('notes')} testid={`fp-pw-notes-${row.id}`}>
            <MessageSquare size={12} />
            {row.notes_count > 0 && <span className="text-[9px] text-amber-400 ml-0.5">{row.notes_count}</span>}
          </IconBtn>
          <IconBtn t="Raw material" onClick={() => onAction('rm')} testid={`fp-pw-rm-${row.id}`}>
            <FlaskConical size={12} />
          </IconBtn>
          <IconBtn t="Edit slot" onClick={() => onAction('edit')} testid={`fp-pw-edit-${row.id}`}>
            <Pencil size={12} />
          </IconBtn>
          <IconBtn t="Duplicate" onClick={() => onAction('duplicate')} testid={`fp-pw-dup-${row.id}`}>
            <Copy size={12} />
          </IconBtn>
          <IconBtn t="Move date" onClick={() => onAction('move')} testid={`fp-pw-move-${row.id}`}>
            <CalendarDays size={12} />
          </IconBtn>
          {fh && !['cancelled', 'completed'].includes(row.lifecycle) && row.status !== 'closed' && (
            <IconBtn t="Cancel slot" danger onClick={() => onAction('cancel')} testid={`fp-pw-cancel-${row.id}`}>
              <Ban size={12} />
            </IconBtn>
          )}
          {fh && (row.status === 'closed' || row.lifecycle === 'cancelled') && (
            <IconBtn t="Reopen into active planning" onClick={() => onAction('reopen')} testid={`fp-pw-reopen-${row.id}`}>
              <RotateCcw size={12} />
            </IconBtn>
          )}
        </div>
      </td>
    );
  }

  if (col.k === 'rm_status') {
    const rm = row.rm_status || {};
    const lvl = rm.level || 'unreserved';
    const tip = (rm.short_materials || []).length
      ? `Short: ${rm.short_materials.map(s => `${s.name} ${s.qty}${s.unit || 'kg'}${s.eta ? ` (ETA ${s.eta})` : ''}`).join(', ')}`
      : rm.level === 'ready' ? 'All raw material covered' : 'Open the RM drawer';
    return (
      <td className="px-2 py-1">
        <button onClick={() => onAction('rm')} title={tip} data-testid={`fp-pw-rmpill-${row.id}`}
          className={`inline-flex flex-col items-start text-[10px] px-2 py-0.5 rounded border ${RM_TONE[lvl] || RM_TONE.unreserved}`}>
          <span>{rm.label || 'Not reserved'}{rm.reserved ? ' 🔒' : ''}</span>
          {rm.short_count > 0 && (
            <span className="text-[9px] opacity-80">{rm.short_count}/{rm.required_count} short</span>
          )}
        </button>
      </td>
    );
  }

  const display = () => {
    switch (col.k) {
      case 'shift': return row.shift_label;
      case 'variance': {
        if (!row.actual_output) return '—';
        const tone = row.variance < -0.001 ? 'text-red-300' : row.variance > 0.001 ? 'text-emerald-300' : 'text-zinc-400';
        return <span className={tone}>{row.variance > 0 ? '+' : ''}{fmtQty(row.variance)} <span className="text-[10px] text-zinc-500">{row.variance_pct ? `${row.variance_pct > 0 ? '+' : ''}${row.variance_pct}%` : ''}</span></span>;
      }
      case 'left_to_make':
        return row.left_to_make > 0.001 ? <span className="text-red-300">{fmtQty(row.left_to_make)}</span> : '—';
      case 'available_made':
        return row.available_made > 0.001 ? <span className="text-emerald-300">{fmtQty(row.available_made)}</span> : '—';
      case 'dispatched':
        return row.dispatched > 0.001 ? <span className="text-blue-300">{fmtQty(row.dispatched)}</span> : '—';
      case 'total_planned': return <span className="text-zinc-100 font-semibold">{fmtQty(row.total_planned)}</span>;
      case 'actual_output': return row.actual_output ? fmtQty(row.actual_output) : '—';
      case 'batch_size': return row.batch_size ? fmtQty(row.batch_size) : '—';
      case 'required_quantity': return row.required_quantity ? fmtQty(row.required_quantity) : '—';
      case 'dispatch_date': return row.dispatch?.planned_date || '—';
      case 'transporter': return row.dispatch?.transporter
        ? `${row.dispatch.transporter}${row.dispatch.vehicle_no ? ` · ${row.dispatch.vehicle_no}` : ''}` : '—';
      case 'status': return <span className={STATUS_TONE[row.status] || 'text-zinc-300'}>{(row.status || '').replace(/_/g, ' ')}</span>;
      case 'priority': return <span className={row.priority === 'critical' ? 'text-red-400' : row.priority === 'high' ? 'text-orange-400' : 'text-zinc-400'}>{row.priority}</span>;
      case 'required_date': {
        const late = row.required_date && row.left_to_make > 0.001 && row.required_date < row.planned_date;
        return <span className={late ? 'text-red-400 font-semibold' : 'text-zinc-300'}>{row.required_date || '—'}</span>;
      }
      default: {
        const v = row[col.k];
        if (v === 0) return '0';
        return v ? String(v) : '—';
      }
    }
  };

  const numeric = col.edit === 'num';
  return (
    <td className={`px-2 py-1 ${align} ${numeric ? 'font-mono' : ''} ${editable ? 'cursor-text' : ''} ${col.tone === 'red' ? 'bg-red-500/[0.03]' : col.tone === 'green' ? 'bg-emerald-500/[0.03]' : col.tone === 'blue' ? 'bg-blue-500/[0.03]' : ''}`}
      data-testid={`fp-pw-cell-${row.id}-${col.k}`}
      onClick={() => { if (editable && !editing) { setDraft(raw(col.k, row) ?? ''); setEditing(true); } }}>
      {!editing && <span className="text-zinc-300">{display()}</span>}
      {editing && col.edit === 'shift' && (
        <select autoFocus value={draft} onChange={e => commit(e.target.value)} onBlur={() => setEditing(false)}
          data-testid={`fp-pw-input-${row.id}-${col.k}`}
          className="w-full bg-zinc-800 border border-amber-500 rounded px-1 py-0.5 text-[11px] text-zinc-100">
          {SHIFTS.map(s => <option key={s.v} value={s.v} className="bg-zinc-900">{s.l}</option>)}
        </select>
      )}
      {editing && col.edit === 'priority' && (
        <select autoFocus value={draft} onChange={e => commit(e.target.value)} onBlur={() => setEditing(false)}
          data-testid={`fp-pw-input-${row.id}-${col.k}`}
          className="w-full bg-zinc-800 border border-amber-500 rounded px-1 py-0.5 text-[11px] text-zinc-100">
          {PRIORITIES.map(p => <option key={p.v} value={p.v} className="bg-zinc-900">{p.l}</option>)}
        </select>
      )}
      {editing && ['text', 'num', 'date'].includes(col.edit) && (
        <input autoFocus type={col.edit === 'num' ? 'number' : col.edit === 'date' ? 'date' : 'text'}
          step="any" value={draft} placeholder={col.ph || ''}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => commit(col.edit === 'num' ? parseFloat(draft || 0) : draft)}
          onKeyDown={e => {
            if (e.key === 'Enter') commit(col.edit === 'num' ? parseFloat(draft || 0) : draft);
            if (e.key === 'Escape') setEditing(false);
          }}
          data-testid={`fp-pw-input-${row.id}-${col.k}`}
          className={`w-full bg-zinc-800 border border-amber-500 rounded px-1 py-0.5 text-[11px] text-zinc-100 ${align}`} />
      )}
    </td>
  );
}

const raw = (k, row) => {
  if (k === 'shift') return row.shift;
  return row[k];
};

function IconBtn({ children, onClick, t, testid, danger }) {
  return (
    <button title={t} onClick={onClick} data-testid={testid}
      className={`p-1 rounded hover:bg-zinc-700/60 flex items-center ${danger ? 'text-red-400' : 'text-zinc-500 hover:text-zinc-100'}`}>
      {children}
    </button>
  );
}

// ─── Expanded detail — order context + R/G/B + dispatch + instruction ─
function ExpandedDetail({ row, colspan }) {
  return (
    <tr className="bg-zinc-900/70">
      <td colSpan={colspan} className="px-6 py-3">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 text-[11px]">
          <div>
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider mb-1">Order context</p>
            <Kv k="Customer" v={row.customer_name || row.purpose_label} />
            <Kv k="Sales order" v={row.sales_order_number || '—'} />
            <Kv k="Required" v={row.required_quantity ? `${fmtQty(row.required_quantity, 'kg')} by ${row.required_date || '—'}` : '—'} />
            <Kv k="Purpose" v={row.purpose_label} />
          </div>
          <div>
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider mb-1">Production</p>
            <Kv k="Batch" v={row.batch_size ? `${fmtQty(row.batch_size, 'kg')} × ${row.batches} = ${fmtQty(row.total_planned, 'kg')}` : `${fmtQty(row.total_planned, 'kg')}`} />
            <Kv k="Output" v={row.actual_output ? fmtQty(row.actual_output, 'kg') : 'not logged'} />
            <Kv k="Variance" v={row.actual_output ? `${row.variance > 0 ? '+' : ''}${fmtQty(row.variance)} kg (${row.variance_pct}%)` : '—'} />
            <Kv k="Shift · Line" v={`${row.shift_label}${row.line_no ? ` · Line ${row.line_no}` : ''}`} />
          </div>
          <div>
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider mb-1">Lifecycle</p>
            <Kv k="🔴 Left to make" v={fmtQty(row.left_to_make, 'kg')} tone="text-red-300" />
            <Kv k="🟢 Made / available" v={fmtQty(row.available_made, 'kg')} tone="text-emerald-300" />
            <Kv k="🔵 Dispatched" v={fmtQty(row.dispatched, 'kg')} tone="text-blue-300" />
            <Kv k="RM" v={`${(row.rm_status || {}).label || '—'}${row.rm_status?.short_count ? ` · ${row.rm_status.short_count} short` : ''}`}
              tone={row.rm_status?.level === 'ready' ? 'text-emerald-300' : row.rm_status?.level === 'short' ? 'text-red-300' : 'text-orange-300'} />
            <Kv k="Earliest feasible" v={row.rm_status?.earliest_feasible_date || '—'} />
            <Kv k="Dispatch" v={row.dispatch?.planned_date
              ? `${row.dispatch.planned_date}${row.dispatch.transporter ? ` · ${row.dispatch.transporter}` : ''}`
              : 'not scheduled'} />
          </div>
          <div>
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider mb-1">Instruction & remarks</p>
            <p className="text-zinc-200 whitespace-pre-wrap">{row.instructions || <span className="text-zinc-600">No production instruction.</span>}</p>
            <p className="text-amber-200/80 mt-1 whitespace-pre-wrap">{row.remarks || <span className="text-zinc-600">No remark.</span>}</p>
            <p className="text-zinc-500 mt-1">{row.notes_count} note(s) · {row.history_count} change(s)</p>
            {(row.rule_flags || []).length > 0 && (
              <div className="mt-2 space-y-1" data-testid={`fp-pw-rules-${row.id}`}>
                <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Manufacturing rules</p>
                {row.rule_flags.map((f, i) => (
                  <p key={i} className={f.level === 'warn' ? 'text-red-300' : 'text-zinc-400'}>
                    ⚠ <span className="font-semibold">{f.title}</span> — {f.text}
                    {f.action && <span className="text-zinc-500"> → {f.action}</span>}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function Kv({ k, v, tone = 'text-zinc-200' }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-zinc-500">{k}</span>
      <span className={`${tone} font-mono text-right`}>{v}</span>
    </div>
  );
}
